import { renderBottleType, useI18N } from '@vivino/js-web-common';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { ComponentSizeType, ComponentSizes } from 'vivino-js/commonPropTypes';
import useDebounce from 'vivino-js/hooks/useDebounce';
import ToolTip, { ALERT, DEFAULT_DURATION } from 'vivino-ui/atoms/ToolTip';

import styles from './quantityPicker.scss';

export const DEFAULT_TIMEOUT_DELAY = 600;

const TRANSLATIONS_PATH = 'components.shared.quantity_picker';
const TRANSLATIONS = {
  onlyXBottlesInStock: `${TRANSLATIONS_PATH}.only_x_bottles_in_stock`,
};

export interface QuantityPickerProps {
  className?: string;
  disabled?: boolean;
  increment: number;
  minimumValue: number;
  maximumValue?: number;
  onQuantityChange: (quantity: number) => void;
  onQuantityChanging?: () => void;
  showVolume?: boolean;
  size?: ComponentSizeType;
  initialValue: number;
  volumePlural?: string;
  volumeSingular?: string;
}

const QuantityPicker = ({
  className,
  disabled = false,
  increment = 1,
  minimumValue = 1,
  maximumValue = null,
  onQuantityChange,
  onQuantityChanging,
  showVolume = false,
  size = ComponentSizes.Base,
  initialValue = 1,
  volumePlural,
  volumeSingular,
}: QuantityPickerProps) => {
  const [value, setValue] = useState(initialValue);
  const [displayValue, setDisplayValue] = useState(undefined);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const [debouncedValue] = useDebounce({ value, delay: DEFAULT_TIMEOUT_DELAY });
  const { t } = useI18N();

  useEffect(() => {
    if (maximumValue && initialValue > maximumValue) {
      setValue(maximumValue);
      setIsTooltipVisible(true);
    } else {
      setValue(initialValue);
    }
  }, [initialValue, maximumValue]);

  useEffect(() => {
    if (value === initialValue) {
      return;
    }

    if (onQuantityChanging) {
      onQuantityChanging();
    }

    setDisplayValue(renderValue());
  }, [value]);

  useEffect(() => {
    if (debouncedValue !== initialValue) {
      onQuantityChange(debouncedValue);
    }
  }, [debouncedValue]);

  const renderValue = () => {
    if (!showVolume) {
      return value;
    }

    return renderBottleType({
      value,
      bottleTypeSingular: volumeSingular,
      bottleTypePlural: volumePlural,
    });
  };

  const handleIncrease = () => {
    setValue((prevValue) => prevValue + increment);
  };

  const handleDecrease = () => {
    setValue((prevValue) => prevValue - increment);
  };

  const showTooltip = () => {
    setIsTooltipVisible(true);
  };

  const closeTooltip = () => {
    setIsTooltipVisible(false);
  };

  if (!Number.isFinite(initialValue)) {
    return null;
  }

  const disableDecrease = value <= minimumValue || disabled;
  const disableIncrease = (maximumValue !== null && value >= maximumValue) || disabled;

  return (
    <div className={cx(styles.outer, className, styles[size])} data-testid="quantityPicker">
      <div
        className={cx(styles.decrease, {
          [styles.disabled]: disableDecrease,
        })}
        onClick={disableDecrease ? null : handleDecrease}
        data-testid="quantityPickerDecrease"
        role="button"
      >
        &#8722;
      </div>
      <div className={styles.value} data-testid="quantityPickerValue">
        {displayValue || renderValue()}
      </div>
      <div
        className={cx(styles.increase, {
          [styles.disabled]: disableIncrease,
        })}
        onClick={disableIncrease ? showTooltip : handleIncrease}
        data-testid="quantityPickerIncrease"
        role="button"
      >
        <div className={styles.errorMessage}>
          <ToolTip
            show={isTooltipVisible}
            closeBtnClassName={styles.closeButton}
            onClose={closeTooltip}
            mode={ALERT}
            duration={DEFAULT_DURATION}
          >
            <label className={styles.textContainer}>
              {t(TRANSLATIONS.onlyXBottlesInStock, {
                stock_count: renderBottleType({
                  value: maximumValue,
                }),
              })}
            </label>
          </ToolTip>
        </div>
        &#43;
      </div>
    </div>
  );
};

export default QuantityPicker;
